import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'

export type LotActionItem = {
  key: string
  label: string
  onSelect: () => void
  disabled?: boolean
  title?: string
  tone?: 'default' | 'danger' | 'dangerArmed'
  /** Keep the menu open after this item (the two-step delete arms first). */
  keepOpen?: boolean
  testId?: string
}

export type LotActionsMenuProps = {
  label: string
  items: LotActionItem[]
  testId?: string
}

const MENU_WIDTH = 240

// Office UX v0 (findings #95): a lot's operations (edit, move, harvest,
// split, merge, story preview, delete) behind one "Acciones" button instead of
// a row of buttons per lot. The menu is `position: fixed` so the lots table's
// horizontal scroll box cannot clip it.
export default function LotActionsMenu({ label, items, testId }: LotActionsMenuProps) {
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)
  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)

  const placeMenu = () => {
    if (!buttonRef.current) return
    const rect = buttonRef.current.getBoundingClientRect()
    const left = Math.min(Math.max(8, rect.right - MENU_WIDTH), Math.max(8, window.innerWidth - MENU_WIDTH - 8))
    // Open upwards when the button sits too low for the menu to fit below it.
    const height = menuRef.current?.offsetHeight ?? items.length * 37 + 12
    const fitsBelow = rect.bottom + 4 + height <= window.innerHeight - 8
    const top = fitsBelow ? rect.bottom + 4 : Math.max(8, rect.top - 4 - height)
    setPosition((current) => (current && current.top === top && current.left === left ? current : { top, left }))
  }

  useLayoutEffect(() => {
    if (open) placeMenu()
  }, [open, position !== null, items.length])

  useEffect(() => {
    if (!open) return
    // The menu follows its button when the page or a panel scrolls.
    const follow = () => placeMenu()
    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node
      if (menuRef.current?.contains(target) || buttonRef.current?.contains(target)) return
      setOpen(false)
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        setOpen(false)
        buttonRef.current?.focus()
      }
    }
    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown, true)
    window.addEventListener('resize', follow)
    window.addEventListener('scroll', follow, true)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown, true)
      window.removeEventListener('resize', follow)
      window.removeEventListener('scroll', follow, true)
    }
  }, [open])

  useEffect(() => {
    if (open && position) menuRef.current?.querySelector<HTMLButtonElement>('button:not([disabled])')?.focus({ preventScroll: true })
  }, [open, position !== null])

  return (
    <span style={{ display: 'inline-block' }} onClick={(event) => event.stopPropagation()}>
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid={testId}
        onClick={() => setOpen((current) => !current)}
        style={{
          padding: '6px 10px',
          borderRadius: 6,
          border: '1px solid #0f766e',
          backgroundColor: open ? '#ccfbf1' : '#ecfdf5',
          color: '#0f766e',
          fontWeight: 600,
          cursor: 'pointer',
          whiteSpace: 'nowrap'
        }}
      >
        {label} ▾
      </button>
      {open && position ? (
        <div
          ref={menuRef}
          role="menu"
          aria-label={label}
          style={{
            position: 'fixed',
            top: position.top,
            left: position.left,
            width: MENU_WIDTH,
            zIndex: 60,
            backgroundColor: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: 10,
            boxShadow: '0 12px 28px rgba(15, 23, 42, 0.18)',
            padding: 6,
            display: 'grid',
            gap: 2
          }}
        >
          {items.map((item) => {
            const danger = item.tone === 'danger' || item.tone === 'dangerArmed'
            return (
              <button
                key={item.key}
                type="button"
                role="menuitem"
                data-testid={item.testId}
                disabled={item.disabled}
                title={item.title}
                onClick={() => {
                  item.onSelect()
                  if (!item.keepOpen) setOpen(false)
                }}
                style={{
                  textAlign: 'left',
                  padding: '8px 10px',
                  borderRadius: 6,
                  border: 'none',
                  backgroundColor: item.tone === 'dangerArmed' ? '#dc2626' : 'transparent',
                  color: item.tone === 'dangerArmed' ? '#ffffff' : danger ? '#b91c1c' : '#0f172a',
                  fontSize: 14,
                  fontWeight: item.tone === 'dangerArmed' ? 700 : 500,
                  cursor: item.disabled ? 'not-allowed' : 'pointer',
                  opacity: item.disabled ? 0.5 : 1
                }}
              >
                {item.label}
              </button>
            )
          })}
        </div>
      ) : null}
    </span>
  )
}
